type WebKitFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};

type WebKitFullscreenVideo = HTMLVideoElement & {
  webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
};

type WebKitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type LockableScreenOrientation = ScreenOrientation & {
  lock?: (orientation: 'landscape') => Promise<void>;
  unlock?: () => void;
};

type NativeScreenOrientationBridge = {
  postMessage: (message: 'landscape' | 'auto') => void;
};

function nativeOrientationBridge(): NativeScreenOrientationBridge | undefined {
  return (window as typeof window & {
    NativeScreenOrientation?: NativeScreenOrientationBridge;
  }).NativeScreenOrientation;
}

export function isLandscapeVideo(video?: HTMLVideoElement | null) {
  return Boolean(video && video.videoWidth > video.videoHeight);
}

function lockLandscapeOrientation() {
  nativeOrientationBridge()?.postMessage('landscape');
  const orientation = screen.orientation as LockableScreenOrientation | undefined;
  if (orientation?.lock) {
    void orientation.lock('landscape').catch(() => undefined);
  }
}

export function lockLandscapePlaybackOrientation() {
  lockLandscapeOrientation();
}

export async function prepareLandscapeHotPlayback() {
  const bridge = nativeOrientationBridge();
  if (bridge) {
    bridge.postMessage('landscape');
    return true;
  }

  const orientation = screen.orientation as LockableScreenOrientation | undefined;
  if (orientation?.lock) {
    try {
      await orientation.lock('landscape');
      return true;
    } catch {
      // Mobile browsers generally require document fullscreen before locking orientation.
    }
  }

  const target = document.documentElement as WebKitFullscreenElement;
  try {
    if (target.requestFullscreen) {
      await target.requestFullscreen();
    } else if (target.webkitRequestFullscreen) {
      await target.webkitRequestFullscreen();
    } else {
      return false;
    }
    if (orientation?.lock) await orientation.lock('landscape');
    return true;
  } catch {
    restoreVideoOrientation();
    return false;
  }
}

export function restoreVideoOrientation() {
  nativeOrientationBridge()?.postMessage('auto');
  const orientation = screen.orientation as LockableScreenOrientation | undefined;
  try {
    orientation?.unlock?.();
  } catch {
    // Some browsers expose Screen Orientation methods but reject them outside fullscreen.
  }
}

export async function requestLandscapeVideoFullscreen(
  video: HTMLVideoElement,
  container?: HTMLElement | null,
) {
  if (!isLandscapeVideo(video)) return false;

  const target = (container || video) as WebKitFullscreenElement;
  const webkitVideo = video as WebKitFullscreenVideo;

  try {
    if (target.requestFullscreen) {
      await target.requestFullscreen();
    } else if (target.webkitRequestFullscreen) {
      await target.webkitRequestFullscreen();
    } else if (webkitVideo.webkitEnterFullscreen) {
      webkitVideo.webkitEnterFullscreen();
    } else {
      return false;
    }
    lockLandscapeOrientation();
    return true;
  } catch {
    restoreVideoOrientation();
    return false;
  }
}

export function observeLandscapeVideoFullscreen(
  video: HTMLVideoElement,
  container?: HTMLElement | null,
) {
  let orientationLocked = false;

  const enterLandscape = () => {
    if (!isLandscapeVideo(video)) return;
    orientationLocked = true;
    lockLandscapeOrientation();
  };
  const leaveLandscape = () => {
    if (!orientationLocked) return;
    orientationLocked = false;
    restoreVideoOrientation();
  };
  const handleFullscreenChange = () => {
    const fullscreenDocument = document as WebKitFullscreenDocument;
    const fullscreenElement = document.fullscreenElement || fullscreenDocument.webkitFullscreenElement;
    if (fullscreenElement === video || fullscreenElement === container) {
      enterLandscape();
      return;
    }
    leaveLandscape();
  };

  video.addEventListener('webkitbeginfullscreen', enterLandscape);
  video.addEventListener('webkitendfullscreen', leaveLandscape);
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

  return () => {
    video.removeEventListener('webkitbeginfullscreen', enterLandscape);
    video.removeEventListener('webkitendfullscreen', leaveLandscape);
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    leaveLandscape();
  };
}

export function exitVideoFullscreen(video?: HTMLVideoElement | null) {
  restoreVideoOrientation();
  const webkitVideo = video as WebKitFullscreenVideo | null | undefined;
  if (webkitVideo?.webkitDisplayingFullscreen && webkitVideo.webkitExitFullscreen) {
    try {
      webkitVideo.webkitExitFullscreen();
    } catch {
      // The native player may already be closing after the ended event.
    }
  }

  const fullscreenDocument = document as WebKitFullscreenDocument;
  if (document.fullscreenElement && document.exitFullscreen) {
    void document.exitFullscreen().catch(() => undefined);
    return;
  }
  if (fullscreenDocument.webkitFullscreenElement && fullscreenDocument.webkitExitFullscreen) {
    try {
      const result = fullscreenDocument.webkitExitFullscreen();
      if (result instanceof Promise) void result.catch(() => undefined);
    } catch {
      // Ignore browsers that revoke fullscreen before the ended handler runs.
    }
  }
}
